import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useApp } from "@/context/AppContext";
import { clearPositionMemory, isInactivityExpired, shouldRedirectHome, syncPositionTtl, touchLastActive } from "@/lib/positionMemory";

// Retour d'arrière-plan (téléphone) ou onglet ré-affiché (web) après plus que le délai admin :
// oublie les positions mémorisées et rouvre le fil en haut, rafraîchi — sauf en plein direct / chat / paiement.
export const PositionMemoryGuard = () => {
  const { config, user, loading } = useApp();
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const userId = user?.id || null;

  useEffect(() => { syncPositionTtl(config); }, [config]);

  useEffect(() => {
    if (loading || !userId) return undefined;
    let lastCheck = 0;
    const check = () => {
      const now = Date.now();
      if (now - lastCheck < 1500) return; // visibilitychange + appStateChange arrivent ensemble
      lastCheck = now;
      if (isInactivityExpired()) {
        clearPositionMemory();
        const here = window.location.pathname;
        // Petit délai : un lien profond (notification) appliqué au réveil garde la main.
        setTimeout(() => {
          if (window.location.pathname !== here) return;
          if (here === "/home") window.dispatchEvent(new Event("yalola:position-expired"));
          else if (shouldRedirectHome(here)) navRef.current("/home", { replace: true });
          window.scrollTo(0, 0);
        }, 400);
      }
      touchLastActive();
    };
    const onVisibility = () => { if (document.visibilityState === "visible") check(); else touchLastActive(); };
    touchLastActive(); // démarrage à froid : la mémoire est déjà vide, on repart de maintenant
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", touchLastActive);
    const tick = setInterval(() => { if (document.visibilityState === "visible") touchLastActive(); }, 60_000);
    let sub = null;
    if (Capacitor.isNativePlatform()) {
      import("@capacitor/app").then(({ App }) => App.addListener("appStateChange", ({ isActive }) => (isActive ? check() : touchLastActive())))
        .then((h) => { sub = h; }).catch(() => {});
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", touchLastActive);
      clearInterval(tick);
      try { sub?.remove?.(); } catch { /* noop */ }
    };
  }, [loading, userId]);

  return null;
};

export default PositionMemoryGuard;
